import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./AdminPanel/Sidebar";

export default function AdminPanel() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [summary, setSummary] = useState({
    organizations: 0,
    activeOrganizations: 0,
    administrators: 0,
    registeredUsers: 0,
  });

  const clearSession = useCallback(() => {
    localStorage.removeItem("admin_access_token");
    localStorage.removeItem("admin_refresh_token");
    localStorage.removeItem("admin_user");
  }, []);

  const loadSummary = useCallback(async (accessToken) => {
    setSummaryLoading(true);

    try {
      const response = await fetch("/tenants/api/list/", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json",
        },
        cache: "no-store",
      });

      let data = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok || data.ok !== true) {
        throw new Error(
          data.message ||
            "No fue posible cargar las organizaciones."
        );
      }

      const organizations = Array.isArray(data.results)
        ? data.results
        : [];

      const activeOrganizations = organizations.filter(
        (tenant) => tenant.is_active === true
      ).length;

      const administrators = organizations.filter(
        (tenant) => Boolean(tenant.admin)
      ).length;

      const registeredUsers = organizations.reduce(
        (total, tenant) =>
          total + Number(tenant.users_count || 0),
        0
      );

      setSummary({
        organizations: organizations.length,
        activeOrganizations,
        administrators,
        registeredUsers,
      });
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "No fue posible cargar el resumen."
      );
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    const initialize = async () => {
      const accessToken = localStorage.getItem(
        "admin_access_token"
      );

      const storedUser = localStorage.getItem("admin_user");

      if (!accessToken || !storedUser) {
        navigate("/admin-panel/login", {
          replace: true,
        });
        return;
      }

      try {
        const parsedUser = JSON.parse(storedUser);

        if (
          parsedUser.user_type !== "SUPERADMIN" ||
          parsedUser.is_superuser !== true
        ) {
          throw new Error("No tienes acceso.");
        }

        setUser(parsedUser);
        await loadSummary(accessToken);
      } catch (error) {
        clearSession();

        setMessage(
          error instanceof Error
            ? error.message
            : "No fue posible cargar la sesión."
        );

        window.setTimeout(() => {
          navigate("/admin-panel/login", {
            replace: true,
          });
        }, 1000);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [
    clearSession,
    loadSummary,
    navigate,
  ]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Sidebar
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        user={user}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          padding: {
            xs: 2,
            sm: 3,
            md: 4,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1400,
            margin: "0 auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              marginBottom: 4,
            }}
          >
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{
                display: {
                  xs: "inline-flex",
                  md: "none",
                },
              }}
            >
              <MenuRoundedIcon />
            </IconButton>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h1"
                sx={{
                  color: "#111111",
                  fontSize: {
                    xs: 25,
                    md: 31,
                  },
                  fontWeight: 800,
                  lineHeight: 1.2,
                }}
              >
                Panel principal
              </Typography>

              <Typography
                sx={{
                  marginTop: 0.5,
                  color: "#6b7280",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {user.name}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: "#ffffff",
              border: "1px solid #d1d5db",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
              }}
            >
              {[
                {
                  label: "Organizaciones",
                  value: summary.organizations,
                },
                {
                  label: "Organizaciones activas",
                  value: summary.activeOrganizations,
                },
                {
                  label: "Administradores",
                  value: summary.administrators,
                },
                {
                  label: "Usuarios registrados",
                  value: summary.registeredUsers,
                },
              ].map((item, index) => (
                <Box
                  key={item.label}
                  sx={{
                    padding: 3,
                    borderRight: {
                      xs: 0,
                      sm:
                        index % 2 === 0
                          ? "1px solid #e5e7eb"
                          : 0,
                      lg:
                        index < 3
                          ? "1px solid #e5e7eb"
                          : 0,
                    },
                    borderBottom: {
                      xs:
                        index < 3
                          ? "1px solid #e5e7eb"
                          : 0,
                      sm:
                        index < 2
                          ? "1px solid #e5e7eb"
                          : 0,
                      lg: 0,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: "#6b7280",
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    {item.label}
                  </Typography>

                  {summaryLoading ? (
                    <CircularProgress
                      size={23}
                      sx={{ marginTop: 1.5 }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        marginTop: 1,
                        color: "#111111",
                        fontSize: 30,
                        fontWeight: 800,
                      }}
                    >
                      {item.value}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={3500}
        onClose={() => setMessage("")}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity="error"
          onClose={() => setMessage("")}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
}